import { useCallback, useState } from 'react';
import { Navigate, Route, useNavigate } from 'react-router-dom';
import useAsync from 'react-use/esm/useAsync';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  CatalogTable,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  scaffolderTranslationRef,
  TemplateWizardPageProps,
} from '@backstage/plugin-scaffolder/alpha';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  Header,
  Page,
  Progress,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import {
  AnalyticsContext,
  configApiRef,
  microsoftAuthApiRef,
  useRouteRef,
  useRouteRefParams,
  useApi,
} from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import {
  scaffolderApiRef,
  ScaffolderFieldExtensions,
  useTemplateSecrets,
} from '@backstage/plugin-scaffolder-react';
import {
  TemplateCard,
  TemplateCardProps,
  useTemplateParameterSchema,
  Workflow,
} from '@backstage/plugin-scaffolder-react/alpha';
import { AzureSubscriptionPickerFieldExtension } from './components/scaffolder/AzureSubscriptionPicker';
import { EnvironmentLocationPickerFieldExtension } from './components/scaffolder/EnvironmentLocationPicker';
import { EnvironmentPickerFieldExtension } from './components/scaffolder/EnvironmentPicker.tsx';
import { AlertBoxFieldExtension } from './components/scaffolder/AlertBox';
// import { SignInProviderConfig } from '@backstage/core-components';

const templateTitleSuffixConfigKey = 'beehive.scaffolder.templateTitleSuffix';

function getOptionalTemplateTitleSuffix(configApi: {
  getOptional(key: string): unknown;
  getOptionalString(key: string): string | undefined;
}): string | undefined {
  if (configApi.getOptional(templateTitleSuffixConfigKey) === '') {
    return undefined;
  }

  return configApi.getOptionalString(templateTitleSuffixConfigKey);
}

export function getTemplateDisplayTitle(
  template: { metadata: { name: string; title?: string } },
  suffix?: string,
): string {
  const baseTitle = template.metadata.title?.trim() || template.metadata.name;
  const trimmedSuffix = suffix?.trim();

  if (!trimmedSuffix) {
    return baseTitle;
  }

  if (baseTitle.toLowerCase().endsWith(trimmedSuffix.toLowerCase())) {
    return baseTitle;
  }

  return `${baseTitle} ${trimmedSuffix}`;
}

const EnvironmentAwareTemplateCard = ({
  template,
  ...props
}: TemplateCardProps) => {
  const configApi = useApi(configApiRef);
  const templateTitleSuffix = getOptionalTemplateTitleSuffix(configApi);

  return (
    <TemplateCard
      {...props}
      onSelected={() => props.onSelected?.(template)} // Intercept and map the callback signature
      template={{
        ...template,
        metadata: {
          ...template.metadata,
          title: getTemplateDisplayTitle(template, templateTitleSuffix),
        },
      }}
    />
  );
};

const EnvironmentAwareTemplateWizardPage = (props: TemplateWizardPageProps) => {
  const rootRef = useRouteRef(scaffolderPlugin.routes.root);
  const taskRoute = useRouteRef(scaffolderPlugin.routes.ongoingTask);
  const configApi = useApi(configApiRef);
  const { secrets: contextSecrets } = useTemplateSecrets();
  const scaffolderApi = useApi(scaffolderApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();
  const { templateName, namespace } = useRouteRefParams(
    scaffolderPlugin.routes.selectedTemplate,
  );
  const { t } = useTranslationRef(scaffolderTranslationRef);
  const templateTitleSuffix = getOptionalTemplateTitleSuffix(configApi);
  const templateRef = stringifyEntityRef({
    kind: 'Template',
    namespace,
    name: templateName,
  });
  const { manifest } = useTemplateParameterSchema(templateRef);
  const { value: templateEntity } = useAsync(
    () => catalogApi.getEntityByRef(templateRef),
    [catalogApi, templateRef],
  );

  const onCreate = useCallback(
    async (values: Record<string, any>) => {
      if (isCreating) {
        return;
      }

      setIsCreating(true);

      const { taskId } = await scaffolderApi.scaffold({
        templateRef,
        values,
        secrets: contextSecrets,
      });

      navigate(taskRoute({ taskId }));
    },
    [
      contextSecrets,
      isCreating,
      navigate,
      scaffolderApi,
      taskRoute,
      templateRef,
    ],
  );

  const onError = useCallback(() => <Navigate to={rootRef()} />, [rootRef]);

  return (
    <AnalyticsContext attributes={{ entityRef: templateRef }}>
      <Page themeId="website">
        <Header
          pageTitleOverride={t('templateWizardPage.pageTitle')}
          title={t('templateWizardPage.title')}
          subtitle={t('templateWizardPage.subtitle')}
          {...props.headerOptions}
        />
        {isCreating && <Progress />}
        <Workflow
          title={getTemplateDisplayTitle(
            {
              metadata: {
                name: templateName,
                title:
                  manifest?.title ??
                  templateEntity?.metadata.title ??
                  undefined,
              },
            },
            templateTitleSuffix,
          )}
          namespace={namespace}
          templateName={templateName}
          onCreate={onCreate}
          components={props.components}
          onError={onError}
          extensions={props.customFieldExtensions}
          formProps={props.formProps}
          layouts={props.layouts}
        />
      </Page>
    </AnalyticsContext>
  );
};

const EnvironmentAwareTemplateEntityRefLink = ({
  entity,
}: {
  entity: Entity;
}) => {
  const configApi = useApi(configApiRef);
  const templateTitleSuffix = getOptionalTemplateTitleSuffix(configApi);
  const displayTitle = getTemplateDisplayTitle(entity, templateTitleSuffix);

  return (
    <EntityRefLink
      entityRef={entity}
      defaultKind="Template"
      title={displayTitle}
    >
      {displayTitle}
    </EntityRefLink>
  );
};

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        providers={[
          {
            id: 'microsoft-auth-provider',
            title: 'Microsoft Azure Entra ID',
            message: 'Sign in with your Microsoft Azure Entra ID',
            apiRef: microsoftAuthApiRef,
          },
        ]}
      />
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route
      path="/catalog"
      element={
        <CatalogIndexPage
          initialKind="template"
          columns={entityListContext => {
            const columns = CatalogTable.defaultColumnsFunc(entityListContext);

            return columns.map(column => {
              if (column.field !== 'resolved.entityRef') {
                return column;
              }

              return {
                ...column,
                customSort: ({ entity: left }, { entity: right }) =>
                  getTemplateDisplayTitle(left).localeCompare(
                    getTemplateDisplayTitle(right),
                  ),
                render: ({ entity }) => (
                  <EnvironmentAwareTemplateEntityRefLink entity={entity} />
                ),
              };
            });
          }}
        />
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          components={{
            TemplateCardComponent: EnvironmentAwareTemplateCard,
            EXPERIMENTAL_TemplateWizardPageComponent:
              EnvironmentAwareTemplateWizardPage,
          }}
        >
          <ScaffolderFieldExtensions>
            <AzureSubscriptionPickerFieldExtension />
            <EnvironmentLocationPickerFieldExtension />
            <EnvironmentPickerFieldExtension />
            <AlertBoxFieldExtension />
          </ScaffolderFieldExtensions>
        </ScaffolderPage>
      }
    />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
